import { ClockIcon } from '@heroicons/react/24/outline';
import { cn } from 'lib/utils';
import * as React from 'react';

export interface TimePickerProps extends Omit<
  React.InputHTMLAttributes<HTMLInputElement>,
  'type' | 'step'
> {}

const TimePicker = React.forwardRef<HTMLInputElement, TimePickerProps>(
  ({ className, ...props }, ref) => {
    const innerRef = React.useRef<HTMLInputElement | null>(null);

    const setRefs = (node: HTMLInputElement | null) => {
      innerRef.current = node;
      if (typeof ref === 'function') {
        ref(node);
      } else if (ref) {
        ref.current = node;
      }
    };

    const openPicker = () => {
      const input = innerRef.current;
      if (!input) return;
      if ('showPicker' in input && typeof input.showPicker === 'function') {
        try {
          input.showPicker();
          return;
        } catch {
          input.focus();
          return;
        }
      }
      input.focus();
    };

    return (
      <div className="relative w-full">
        <input
          ref={setRefs}
          type="time"
          step={60}
          className={cn(
            'block w-full rounded-md border border-gray-300 bg-white py-2 pr-3 text-sm font-light shadow-sm placeholder-gray-400 focus:border-purple-500 focus:outline-none focus:ring-1 focus:ring-purple-500 disabled:cursor-not-allowed disabled:opacity-50',
            className,
            'appearance-none pl-10 [&::-webkit-calendar-picker-indicator]:hidden [&::-webkit-calendar-picker-indicator]:appearance-none',
          )}
          {...props}
        />
        <button
          type="button"
          tabIndex={-1}
          onClick={openPicker}
          disabled={props.disabled}
          className="absolute inset-y-0 left-0 flex items-center px-3 text-gray-400 hover:text-gray-600 disabled:opacity-50"
          aria-label="Open time picker"
        >
          <ClockIcon className="h-4 w-4" />
        </button>
      </div>
    );
  },
);

TimePicker.displayName = 'TimePicker';

export { TimePicker };
